import React, { useCallback, useEffect, useState } from "react";
import { Building2, Loader2, Check, Clock } from "lucide-react";
import api from "../../api";
import { useLang } from "../../context/LanguageContext";
import { useToast } from "../../hooks/use-toast";

// A chain that has outgrown the fair-use ceiling should be able to say so here, not hunt for an
// email address. Admin approves it in one tap and their limits change immediately.
export const ChainEnquiry = ({ dealLimit, branchLimit }) => {
  const { t } = useLang();
  const { toast } = useToast();
  const [state, setState] = useState(null);
  const [open, setOpen] = useState(false);
  const [deals, setDeals] = useState("");
  const [branches, setBranches] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/my/limit-request").then((r) => setState(r.data)).catch(() => setState({ exists: false }));
  }, []);
  useEffect(load, [load]);

  const send = async () => {
    setBusy(true);
    try {
      await api.post("/my/limit-request", {
        deals: Number(deals) || undefined, branches: Number(branches) || undefined, note: note.trim() });
      setOpen(false); setNote("");
      toast({ title: t("cq.sentTitle"), description: t("cq.sentNote") });
      load();
    } catch (e) { toast({ title: t("cq.failed"), description: e.response?.data?.detail }); }
    finally { setBusy(false); }
  };

  if (!state) return null;

  if (state.exists && state.status === "new") {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-muted-foreground" data-testid="chain-enquiry-pending">
        <Clock size={12} /> {t("cq.pending")}
      </span>
    );
  }

  return (
    <>
      <button onClick={() => setOpen(true)} data-testid="chain-enquiry-open"
        className="text-xs font-semibold text-[#16a34a] hover:underline inline-flex items-center gap-1.5">
        <Building2 size={12} /> {t("cq.need")}
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" data-testid="chain-enquiry-sheet">
          <div className="w-full max-w-md bg-card border border-border rounded-3xl p-5 shadow-2xl">
            <h2 className="font-extrabold text-foreground">{t("cq.title")}</h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              {t("cq.sub", { deals: dealLimit ?? 100, branches: branchLimit ?? 25 })}
            </p>
            <div className="mt-4 grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-muted-foreground">{t("cq.deals")}</label>
                <input value={deals} onChange={(e) => setDeals(e.target.value.replace(/\D/g, ""))} inputMode="numeric"
                  data-testid="chain-enquiry-deals" placeholder={String(dealLimit ?? 100)}
                  className="mt-1 w-full bg-secondary rounded-xl px-3 py-2 text-sm outline-none" />
              </div>
              <div>
                <label className="text-xs font-semibold text-muted-foreground">{t("cq.branches")}</label>
                <input value={branches} onChange={(e) => setBranches(e.target.value.replace(/\D/g, ""))} inputMode="numeric"
                  data-testid="chain-enquiry-branches" placeholder={String(branchLimit ?? 25)}
                  className="mt-1 w-full bg-secondary rounded-xl px-3 py-2 text-sm outline-none" />
              </div>
            </div>
            <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} data-testid="chain-enquiry-note"
              placeholder={t("cq.notePlaceholder")} maxLength={1000}
              className="mt-3 w-full bg-secondary rounded-xl px-3 py-2.5 text-sm outline-none resize-none" />
            <div className="mt-3 flex gap-2">
              <button onClick={() => setOpen(false)} data-testid="chain-enquiry-cancel"
                className="flex-1 text-sm font-semibold border border-border hover:bg-secondary rounded-xl py-2.5 transition-colors">{t("fp.notNow")}</button>
              <button onClick={send} disabled={busy || (!deals && !branches)} data-testid="chain-enquiry-send"
                className="flex-1 bg-[#16a34a] hover:bg-[#15803d] disabled:opacity-60 text-white text-sm font-bold rounded-xl py-2.5 transition-colors inline-flex items-center justify-center gap-2">
                {busy ? <Loader2 className="animate-spin" size={14} /> : <Check size={14} />} {t("cq.send")}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ChainEnquiry;
